/**
 * Cards — reusable importable card components used across pages.
 *
 * Currently exports:
 *   - `EventCard`: the standard event tile rendered on the events index page,
 *     organization detail page, and any other surface that needs the same
 *     visual treatment. Takes a single `EventListItem` plus a click handler
 *     and an optional `isSaved` flag.
 */

import React, { useState } from "react";
import { Box, Card, CardContent, CardMedia, Chip, Typography, Button, Skeleton } from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import VideoCallIcon from "@mui/icons-material/VideoCall";
import LanguageIcon from "@mui/icons-material/Language";
import { categoryColors, common, colors } from "@/theme/colors";
import { assetUrl } from "@/utils/asset";
import { isImageCached, markImageLoaded } from "@/utils/imageCache";
import SaveButton from "@/components/Importable/SaveButton";
import { useIsSaved } from "@/hooks/useIsSaved";
import { cardActivationProps } from "@/utils/cardA11y";

/** Translucent grey marking a past event — used by the greyed-out
 *  "Past Event" button. */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const PAST_OVERLAY = "rgba(128, 128, 128, 0.40)";

/** Solid (opaque) light grey used as the date badge fill so the date reads as a
 *  solid block — no image showing through — on every event. */
const DATE_BADGE_BG = "rgb(168, 168, 168)";
const StyledEventCard = styled(Card)(({
  theme
}) => ({
  // Fill the wrapping Box so all cards in the same flex row match the
  // tallest card (driven by header/tagline length variations).
  height: "100%",
  width: "340px",
  display: "flex",
  flexDirection: "column",
  cursor: "pointer",
  border: `2px solid transparent`,
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[2],
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: theme.shadows[10]
  },
  [theme.breakpoints.down("sm")]: {
    width: "min(92vw, 330px)"
  },
  "@media (max-width: 375px)": {
    width: "min(92vw, 320px)"
  }
}));
export const CategoryChip = styled(Chip, {
  shouldForwardProp: prop => prop !== "category"
})(({
  category
}) => {
  const getColor = () => {
    const key = String(category || "").toLowerCase();
    return categoryColors[key] || categoryColors.default;
  };
  return {
    backgroundColor: getColor(),
    color: common.white,
    fontWeight: "bold",
    "&:hover": {
      backgroundColor: getColor(),
      opacity: 0.8
    }
  };
});

/**
 * Valid event types — the keys of `categoryColors` minus the `default`
 * fallback: workshop, conference, trade show, networking, training, webinar.
 * Anything else (e.g. a stray "in-person event" format value mis-stored as a
 * type) is not a real event type.
 */
export const VALID_EVENT_TYPES = Object.keys(categoryColors).filter(k => k !== "default");
const VALID_EVENT_TYPE_SET = new Set(VALID_EVENT_TYPES);

/**
 * Split a pipe-delimited `category` string into its individual event types,
 * keeping only values within the valid list (case-insensitive). Sanitizes away
 * false types so they never render as a chip on a card or in the filter bar.
 */
export const sanitizeEventTypes = category => String(category || "").split(" | ").map(c => c.trim()).filter(c => c && VALID_EVENT_TYPE_SET.has(c.toLowerCase()));
const getCategoryColor = category => {
  // Color by the first *valid* type so a stray false type (e.g.
  // "in-person event") doesn't grey out the card's accent.
  const first = (sanitizeEventTypes(category)[0] || "").toLowerCase();
  return categoryColors[first] || categoryColors.default;
};

/** Three-letter, upper-cased month abbreviation for the date badge ("JUN"). */
const formatMonthAbbr = dateString => {
  if (!dateString) return "TBA";
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return "TBA";
  return d.toLocaleDateString("en-US", {
    month: "short",
    timeZone: "UTC"
  }).toUpperCase();
};

/** Day-of-month for the date badge ("23"). */
const formatDay = dateString => {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", {
    day: "numeric",
    timeZone: "UTC"
  });
};

/**
 * Returns true when the supplied event ended *before* today.
 * Same-day events count as current. Uses `endDate` if present, otherwise `date`.
 */
export const isEventPast = event => {
  const refDate = event.endDate || event.date;
  if (!refDate) return false;
  const end = new Date(refDate);
  if (isNaN(end.getTime())) return false;
  // Treat date-only strings ("YYYY-MM-DD") as end-of-day so the same
  // calendar day is still considered current.
  if (refDate.length <= 10) end.setHours(23, 59, 59, 999);
  // Compare against the start of today so any time on the event date counts
  // as current.
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  return end.getTime() < startOfToday.getTime();
};
const formatPrice = (price, currency) => {
  const p = Number.isFinite(price) ? price : 0;
  const cur = typeof currency === "string" && currency.trim() ? currency.trim() : "USD";
  if (p === 0) return "Free";
  return `${cur === "USD" ? "$" : cur}${p}`;
};
export const EventCard = ({
  event,
  onClick,
  dataSelectTarget,
  isPast,
  variant = "grid"
}) => {
  const isList = variant === "list";
  const catColor = getCategoryColor(event.category);
  const past = isPast ?? isEventPast(event);
  // Saved-state border: coloured only when saved, otherwise transparent —
  // matching the organization cards' behaviour.
  const saved = useIsSaved("event", event.id || event.slug, event.slug);
  const imgSrc = assetUrl(event.image);
  // Skip the loading skeleton for URLs already confirmed good this session.
  const [imgLoaded, setImgLoaded] = useState(() => isImageCached(imgSrc));
  const [imgError, setImgError] = useState(false);
  return /*#__PURE__*/_jsx(Box, {
    sx: {
      position: "relative",
      flexShrink: 0,
      // Stretch within the parent flex row so sibling cards share
      // the height of the tallest one.
      display: "flex",
      alignSelf: "stretch",
      height: "auto",
      width: isList ? "100%" : undefined
    },
    children: /*#__PURE__*/_jsxs(StyledEventCard, {
      "data-select-target": dataSelectTarget,
      ...cardActivationProps(`Open ${event.header}`, () => onClick(event)),
      sx: {
        borderColor: saved ? catColor : "transparent",
        ...(isList && {
          width: "100%",
          flexDirection: {
            xs: "column",
            sm: "row"
          }
        }),
        // Hover matches the professionals cards: elevation 10 +
        // the page accent (events green) at half alpha. Saved
        // cards keep their solid category border.
        "&:hover": {
          boxShadow: 10,
          borderColor: saved ? catColor : alpha(colors.green.primary, 0.5)
        }
      },
      children: [/*#__PURE__*/_jsxs(Box, {
        sx: {
          position: "relative",
          ...(isList && {
            width: {
              xs: "100%",
              sm: 280
            },
            flexShrink: 0
          })
        },
        children: [!imgLoaded && !imgError && /*#__PURE__*/_jsx(Skeleton, {
          variant: "rectangular",
          width: "100%",
          height: 200
        }), imgError && /*#__PURE__*/_jsx(Box, {
          sx: {
            width: "100%",
            height: 200,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "grey.100"
          }
        }), /*#__PURE__*/_jsx(CardMedia, {
          component: "img",
          image: imgSrc,
          alt: event.header,
          onLoad: () => {
            markImageLoaded(imgSrc);
            setImgLoaded(true);
          },
          onError: () => setImgError(true),
          sx: {
            display: imgError ? "none" : "block",
            width: "100%",
            height: isList ? {
              xs: 200,
              sm: "100%"
            } : 200,
            objectFit: "cover"
          }
        }), /*#__PURE__*/_jsx(SaveButton, {
          resourceType: "event",
          resourceId: event.id || event.slug,
          resourceSlug: event.slug,
          resourceTitle: event.header,
          resourceSummary: event.description,
          resourceImage: event.image?.startsWith("data:") ? null : assetUrl(event.image),
          color: catColor
        }), /*#__PURE__*/_jsxs(Box, {
          sx: {
            position: "absolute",
            top: 0,
            left: 0,
            zIndex: 1,
            width: 70,
            height: 70,
            backgroundColor: DATE_BADGE_BG,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            lineHeight: 1
          },
          children: [/*#__PURE__*/_jsx(Typography, {
            component: "span",
            sx: {
              color: "text.secondary",
              fontWeight: 700,
              // Per-breakpoint object so the body1 variant's
              // responsive font size can't override it at sm+.
              fontSize: {
                xs: "1.05rem",
                sm: "1.05rem",
                md: "1.05rem",
                lg: "1.05rem"
              },
              lineHeight: 1.1,
              letterSpacing: "0.5px"
            },
            children: formatMonthAbbr(event.date)
          }), /*#__PURE__*/_jsx(Typography, {
            component: "span",
            sx: {
              color: "text.secondary",
              fontWeight: 700,
              // Per-breakpoint object — a plain fontSize is
              // overridden by the body1 variant at sm+.
              fontSize: {
                xs: "2.1rem",
                sm: "2.1rem",
                md: "2.1rem",
                lg: "2.1rem"
              },
              lineHeight: 1
            },
            children: formatDay(event.date)
          })]
        }), /*#__PURE__*/_jsx(Box, {
          sx: {
            position: "absolute",
            bottom: 8,
            left: 8,
            display: "flex",
            flexWrap: "wrap",
            gap: 0.5,
            zIndex: 1
          },
          children: sanitizeEventTypes(event.category).map(cat => /*#__PURE__*/_jsx(CategoryChip, {
            label: cat,
            size: "small",
            category: cat
          }, cat))
        })]
      }), /*#__PURE__*/_jsxs(CardContent, {
        sx: {
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0
        },
        children: [/*#__PURE__*/_jsx(Typography, {
          variant: "h6",
          component: "h3",
          gutterBottom: true,
          sx: {
            color: catColor,
            textTransform: "uppercase",
            fontSize: "1.25em",
            lineHeight: 1.2,
            fontWeight: 700
          },
          children: event.header
        }), event.subheader && /*#__PURE__*/_jsx(Typography, {
          variant: "subtitle2",
          sx: {
            color: "text.primary",
            mb: 1,
            textTransform: "none"
          },
          gutterBottom: true,
          children: event.subheader
        }), isList && event.description && /*#__PURE__*/_jsx(Typography, {
          variant: "body2",
          color: "text.secondary",
          sx: {
            mb: 1.5,
            display: "-webkit-box",
            WebkitLineClamp: {
              xs: 3,
              sm: 4,
              md: 5
            },
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis"
          },
          children: event.description
        }), /*#__PURE__*/_jsxs(Box, {
          sx: {
            mt: "auto"
          },
          children: [/*#__PURE__*/_jsxs(Box, {
            sx: {
              display: "flex",
              flexDirection: "row",
              gap: 2,
              mb: 2,
              alignItems: "flex-start"
            },
            children: [/*#__PURE__*/_jsxs(Box, {
              sx: {
                display: "flex",
                flexWrap: "wrap",
                alignContent: "flex-start",
                gap: 0.5,
                flex: "1 1 auto",
                minWidth: 0
              },
              children: [/*#__PURE__*/_jsx(Chip, {
                size: "small",
                variant: "outlined",
                icon: event.isVirtual || event.isHybrid ? /*#__PURE__*/_jsx(VideoCallIcon, {}) : /*#__PURE__*/_jsx(LocationOnIcon, {}),
                label: event.isHybrid ? "Hybrid Event" : event.isVirtual ? "Virtual Event" : [event.location?.city, event.location?.country || "USA"].filter(Boolean).join(", ") || "TBA",
                sx: {
                  maxWidth: "100%",
                  // 15% smaller than the default small chip.
                  fontSize: "0.69rem"
                }
              }), /*#__PURE__*/_jsx(Chip, {
                size: "small",
                variant: "outlined",
                icon: /*#__PURE__*/_jsx(LanguageIcon, {}),
                label: event.language || "English",
                sx: {
                  maxWidth: "100%",
                  // 15% smaller than the default small chip.
                  fontSize: "0.69rem"
                }
              })]
            }), /*#__PURE__*/_jsxs(Box, {
              sx: {
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                flexShrink: 0,
                maxWidth: "45%",
                gap: 0.5
              },
              children: [/*#__PURE__*/_jsx(Typography, {
                variant: "caption",
                sx: {
                  color: "text.secondary",
                  lineHeight: 1.2
                },
                children: "Organized By"
              }), /*#__PURE__*/_jsxs(Box, {
                sx: {
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  gap: 0.5
                },
                children: [event.organization?.logo && /*#__PURE__*/_jsx(Box, {
                  component: "img",
                  src: event.organization.logo,
                  alt: event.organization.name,
                  sx: {
                    width: 32,
                    height: 32,
                    borderRadius: "5px",
                    objectFit: "cover",
                    flexShrink: 0
                  }
                }), /*#__PURE__*/_jsx(Typography, {
                  variant: "body2",
                  sx: {
                    fontWeight: 600,
                    color: "text.primary",
                    textAlign: "right",
                    lineHeight: 1.2,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden"
                  },
                  children: event.organization?.name || event.organizer?.name || "Unknown"
                })]
              })]
            })]
          }), /*#__PURE__*/_jsxs(Box, {
            sx: {
              display: "flex",
              alignItems: "stretch",
              justifyContent: "space-between",
              gap: 1
            },
            children: [/*#__PURE__*/_jsx(Typography, {
              component: "span",
              sx: {
                // Filled pill — white text on the category
                // color. Stretches to the button's height
                // (row alignItems: stretch); px matches the
                // button's horizontal padding.
                display: "flex",
                alignItems: "center",
                fontWeight: 700,
                color: common.white,
                backgroundColor: catColor,
                fontSize: "1.1rem",
                lineHeight: 1,
                whiteSpace: "nowrap",
                flexShrink: 0,
                borderRadius: 1,
                px: 2
              },
              children: `Price - ${formatPrice(event.price, event.currency)}`
            }), /*#__PURE__*/_jsx(Button, {
              variant: "outlined",
              onClick: e => {
                e.stopPropagation();
                onClick(event);
              },
              sx: {
                width: "50%",
                flexShrink: 0,
                // Past events: grey overlay fill on the
                // button only (the card itself is no longer
                // dimmed) to flag the past state.
                borderColor: past ? "grey.400" : catColor,
                color: past ? "text.secondary" : catColor,
                backgroundColor: past ? PAST_OVERLAY : "transparent",
                "&:hover": {
                  borderColor: past ? "grey.400" : catColor,
                  bgcolor: past ? PAST_OVERLAY : alpha(catColor, 0.08)
                }
              },
              children: past ? "Past Event" : "View Details"
            })]
          })]
        })]
      })]
    })
  });
};
export default EventCard;

// ─── RegulationCard ─────────────────────────────────────────────────────────

/**
 * Plain-text excerpt from stored brief/article HTML, for index-card
 * descriptions. Table blocks are dropped entirely (their cell text reads
 * as label soup, not prose), block closers become sentence breaks, tags
 * are stripped, and entities/whitespace normalized. Regex-based on
 * purpose: it runs during SSR where DOMParser does not exist.
 */
const htmlToPlainExcerpt = html => {
  if (!html || !/[<&]/.test(html)) return html;
  let text = html.replace(/<table[\s\S]*?<\/table>/gi, " ").replace(/<(style|script)[\s\S]*?<\/\1>/gi, " ").replace(/<\/(p|div|h[1-6]|li|section|blockquote)>/gi, ". ").replace(/<br\s*\/?>/gi, " ").replace(/<[^>]+>/g, " ").replace(/&nbsp;/gi, " ").replace(/&amp;/gi, "&").replace(/&lt;/gi, "<").replace(/&gt;/gi, ">").replace(/&(#39|apos);/gi, "'").replace(/&quot;/gi, '"').replace(/\s+/g, " ").replace(/\s+([.,;:!?])/g, "$1").replace(/(\. )+/g, ". ")
  // A paragraph ending in "." plus the injected block break makes
  // ".." — collapse repeats back to one.
  .replace(/\.(\s*\.)+/g, ".").replace(/^[\s.,;:]+/, "").trim();
  // A table-only body strips to punctuation crumbs — treat as empty so
  // the caller's "No description available" fallback applies.
  return text.length < 20 ? "" : text;
};
const formatRegDate = value => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric"
  });
};
const RegulationCardBase = ({
  regulation,
  onClick,
  accentColor,
  highlightedTags = [],
  hideTypeChip = false,
  listMode = false
}) => {
  const hasDynamicStatus = regulation.isNew !== undefined;
  const isNew = regulation.isNew;
  // Saved-state border: accent colour only when saved, else transparent.
  const saved = useIsSaved("regulation", regulation.id, String(regulation.id));
  const description = htmlToPlainExcerpt(regulation.executiveSummary || "") || htmlToPlainExcerpt(regulation.description || "") || htmlToPlainExcerpt(regulation.sections?.[0]?.content || "") || (
  // The index payload carries the body as an HTML string — excerpt
  // it to prose (tables dropped, tags stripped) before display.
  typeof regulation.content === "string" ? htmlToPlainExcerpt(regulation.content) : "") || "";
  // List rows keep the long text (the clamp is the safe area); grid
  // cards stay on the short 150-char blurb.
  const charCap = listMode ? 1500 : 150;
  const truncatedDesc = description.length > charCap ? description.substring(0, charCap) + "…" : description || "No description available";
  const displayTags = (regulation.tags || []).slice(0, 3);
  const regionSet = new Set([...(regulation.regionTags || []), ...(regulation.regions || [])]);
  const filterSet = new Set(regulation.filterTags || []);
  const highlightedSet = new Set(highlightedTags);
  const publishedDisplay = formatRegDate(regulation.publishedAt);
  const updatedDisplay = formatRegDate(regulation.lastUpdated);
  return /*#__PURE__*/_jsxs(Card, {
    ...(onClick ? cardActivationProps(`Open ${regulation.title}`, onClick) : {}),
    sx: {
      position: "relative",
      // Saved-state border: the accent colour shows only when the
      // regulation is saved, otherwise transparent — matching the
      // organization cards.
      border: `2px solid ${saved ? accentColor || colors.blue.primary : "transparent"}`,
      borderRadius: 1,
      // Three per row at md+, matching the organizations cards:
      // basis of a third minus the grid's column gap, capped at
      // the same 425px so the two card families line up.
      flex: {
        xs: "1 1 100%",
        sm: "1 1 calc(50% - 12px)",
        md: "1 1 calc(33.333% - 14px)"
      },
      minWidth: {
        xs: "280px",
        md: "300px"
      },
      maxWidth: {
        xs: "100%",
        md: 425
      },
      display: "flex",
      flexDirection: "column",
      flexShrink: 1,
      cursor: onClick ? "pointer" : "default",
      transition: "all 0.3s ease",
      boxShadow: 2,
      // Hover matches the professionals cards: elevation 10 + the
      // page accent at half alpha; saved cards keep the solid border.
      "&:hover": onClick ? {
        boxShadow: 10,
        borderColor: saved ? accentColor || colors.blue.primary : alpha(accentColor || colors.blue.primary, 0.5)
      } : undefined
    },
    children: [/*#__PURE__*/_jsx(SaveButton, {
      resourceType: "regulation",
      resourceId: regulation.id,
      resourceSlug: String(regulation.id),
      resourceTitle: regulation.title,
      resourceSummary: description,
      color: colors.blue.primary
    }), /*#__PURE__*/_jsxs(CardContent, {
      sx: {
        display: "flex",
        flexDirection: "column",
        gap: 1,
        flexGrow: 1,
        p: 3,
        "&:last-child": {
          pb: 3
        }
      },
      children: [!hideTypeChip && regulation.regulationType && /*#__PURE__*/_jsx(Box, {
        sx: {
          mb: 0.5,
          pr: 5
        },
        children: /*#__PURE__*/_jsx(Chip, {
          label: regulation.regulationType,
          variant: "outlined",
          size: "small",
          sx: {
            fontWeight: 600
          }
        })
      }), /*#__PURE__*/_jsxs(Box, {
        sx: {
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 0.75,
          pr: 5
        },
        children: [/*#__PURE__*/_jsx(Typography, {
          variant: "h6",
          component: "h3",
          sx: {
            fontWeight: 600,
            color: accentColor || "primary.main"
          },
          children: regulation.shortTitle || regulation.title
        }), hasDynamicStatus && /*#__PURE__*/_jsx(Chip, {
          label: isNew ? "NEW" : "UPDATED",
          size: "small",
          color: isNew ? "success" : "warning"
        })]
      }), /*#__PURE__*/_jsx(Typography, {
        variant: "body2",
        color: "text.secondary",
        sx: {
          flexGrow: 1,
          display: "-webkit-box",
          // Scales with the card: wider cards fit more per
          // line, so more lines before truncation earns its
          // keep. Height comes from minHeight below rather
          // than a fixed clamp at every breakpoint.
          WebkitLineClamp: listMode ? {
            xs: 4,
            sm: 6,
            md: 8
          } : {
            xs: 4,
            md: 6,
            lg: 7
          },
          minHeight: listMode ? "auto" : {
            xs: "auto",
            md: "7.5rem"
          },
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          textOverflow: "ellipsis"
        },
        children: truncatedDesc
      }), (regulation.category || displayTags.length > 0) && /*#__PURE__*/_jsxs(Box, {
        sx: {
          display: "flex",
          flexWrap: "wrap",
          gap: 0.5,
          mt: 0.5
        },
        children: [regulation.category && /*#__PURE__*/_jsx(Chip, {
          label: regulation.category,
          size: "small",
          color: "primary",
          variant: "outlined"
        }), displayTags.map(tag => {
          const highlighted = highlightedSet.has(tag);
          // Color by category so the chip keeps its hue:
          // regions purple, disciplines green, else blue —
          // filled when toggled on, outlined when off.
          const tagColor = regionSet.has(tag) ? "secondary" : filterSet.has(tag) ? "success" : "primary";
          return /*#__PURE__*/_jsx(Chip, {
            label: tag,
            size: "small",
            variant: highlighted ? "filled" : "outlined",
            color: tagColor
          }, tag);
        })]
      }), /*#__PURE__*/_jsxs(Box, {
        sx: {
          mt: "auto"
        },
        children: [publishedDisplay && /*#__PURE__*/_jsxs(Typography, {
          variant: "caption",
          color: "text.secondary",
          sx: {
            display: "block"
          },
          children: ["Published: ", publishedDisplay]
        }), publishedDisplay && updatedDisplay && updatedDisplay !== publishedDisplay && /*#__PURE__*/_jsxs(Typography, {
          variant: "caption",
          color: "text.secondary",
          sx: {
            display: "block"
          },
          children: ["Last Updated: ", updatedDisplay]
        }), !publishedDisplay && updatedDisplay && /*#__PURE__*/_jsxs(Typography, {
          variant: "caption",
          color: "text.secondary",
          sx: {
            display: "block"
          },
          children: ["Last Updated: ", updatedDisplay]
        })]
      })]
    })]
  });
};
export const RegulationCard = /*#__PURE__*/React.memo(RegulationCardBase);

// ─── ArticleCard ─────────────────────────────────────────────────────────────

const formatArticleDate = value => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric"
  });
};
export const ArticleCard = ({
  article,
  onClick,
  accentColor = "#7b1fa2",
  highlightedTags = [],
  showReadMore = false,
  listMode = false
}) => {
  const isNavigable = Boolean(onClick);
  // Saved-state border: accent colour only when saved, else transparent.
  const saved = useIsSaved("article", article.id, article.slug || String(article.id ?? ""));
  const description = article.executiveSummary || article.description || (typeof article.content === "string" ? htmlToPlainExcerpt(article.content) : "") || "No description available";
  // List rows keep the long text; grid cards the short blurb.
  const articleCharCap = listMode ? 1500 : 150;
  const truncatedDesc = description.length > articleCharCap ? description.substring(0, articleCharCap) + "…" : description;
  const displayTags = (article.tags || []).slice(0, 3);
  const highlightedSet = new Set(highlightedTags);
  const publishedDisplay = formatArticleDate(article.publishedAt);
  return /*#__PURE__*/_jsxs(Card, {
    ...(isNavigable && onClick ? cardActivationProps(`Open ${article.title}`, onClick) : {}),
    sx: {
      position: "relative",
      // Saved-state border: the accent colour shows only when the
      // article is saved, otherwise transparent — matching the
      // organization cards.
      border: `2px solid ${saved ? accentColor : "transparent"}`,
      borderRadius: 1,
      // Same 3-up sizing as the regulation cards.
      flex: {
        xs: "1 1 100%",
        sm: "1 1 calc(50% - 12px)",
        md: "1 1 calc(33.333% - 14px)"
      },
      minWidth: {
        xs: "280px",
        md: "300px"
      },
      maxWidth: {
        xs: "100%",
        md: 425
      },
      display: "flex",
      flexDirection: "column",
      flexShrink: 1,
      cursor: isNavigable ? "pointer" : "default",
      transition: "all 0.3s ease",
      boxShadow: 2,
      backgroundColor: "background.paper",
      "&:hover": isNavigable ? {
        boxShadow: 10,
        borderColor: saved ? accentColor : alpha(accentColor, 0.5)
      } : undefined
    },
    children: [/*#__PURE__*/_jsx(SaveButton, {
      resourceType: "article",
      resourceId: article.id,
      resourceSlug: article.slug || String(article.id ?? ""),
      resourceTitle: article.title,
      resourceSummary: article.executiveSummary || article.description || null,
      resourceImage: article.authorPhotoUrl ? assetUrl(article.authorPhotoUrl) : null,
      color: colors.purple.primary
    }), /*#__PURE__*/_jsxs(CardContent, {
      sx: {
        display: "flex",
        flexDirection: "column",
        gap: 1,
        flexGrow: 1,
        p: 3,
        "&:last-child": {
          pb: 3
        }
      },
      children: [article.isNew && /*#__PURE__*/_jsx(Box, {
        sx: {
          mb: 0.5,
          pr: 5
        },
        children: /*#__PURE__*/_jsx(Chip, {
          label: "NEW",
          size: "small",
          color: "success"
        })
      }), /*#__PURE__*/_jsx(Typography, {
        variant: "h6",
        component: "h3",
        sx: {
          fontWeight: 600,
          color: accentColor,
          pr: 5
        },
        children: article.shortTitle || article.title
      }), article.authorName && /*#__PURE__*/_jsxs(Typography, {
        variant: "caption",
        sx: {
          color: "text.secondary",
          fontStyle: "italic"
        },
        children: ["By ", article.authorName]
      }), /*#__PURE__*/_jsx(Typography, {
        variant: "body2",
        color: "text.secondary",
        sx: {
          flexGrow: 1,
          display: "-webkit-box",
          // Scales with the card: wider cards fit more per
          // line, so more lines before truncation earns its
          // keep. Height comes from minHeight below rather
          // than a fixed clamp at every breakpoint.
          WebkitLineClamp: listMode ? {
            xs: 4,
            sm: 6,
            md: 8
          } : {
            xs: 4,
            md: 6,
            lg: 7
          },
          minHeight: listMode ? "auto" : {
            xs: "auto",
            md: "7.5rem"
          },
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          textOverflow: "ellipsis"
        },
        children: truncatedDesc
      }), displayTags.length > 0 && /*#__PURE__*/_jsx(Box, {
        sx: {
          display: "flex",
          flexWrap: "wrap",
          gap: 0.5,
          mt: 0.5
        },
        children: displayTags.map(tag => {
          const highlighted = highlightedSet.has(tag);
          return /*#__PURE__*/_jsx(Chip, {
            label: tag,
            size: "small",
            variant: highlighted ? "filled" : "outlined",
            sx: highlighted ? {
              bgcolor: accentColor,
              color: "#fff"
            } : {
              borderColor: accentColor,
              color: accentColor
            }
          }, tag);
        })
      }), /*#__PURE__*/_jsxs(Box, {
        sx: {
          mt: "auto"
        },
        children: [publishedDisplay && /*#__PURE__*/_jsxs(Typography, {
          variant: "caption",
          color: "text.secondary",
          sx: {
            display: "block",
            mb: showReadMore ? 1 : 0
          },
          children: ["Published: ", publishedDisplay]
        }), showReadMore && isNavigable && /*#__PURE__*/_jsx(Box, {
          sx: {
            textAlign: "right"
          },
          children: /*#__PURE__*/_jsx(Button, {
            variant: "outlined",
            size: "small",
            sx: {
              color: accentColor,
              borderColor: accentColor,
              "&:hover": {
                borderColor: accentColor,
                backgroundColor: alpha(accentColor, 0.08)
              }
            },
            onClick: e => {
              e.stopPropagation();
              onClick?.();
            },
            children: "Read Article"
          })
        })]
      })]
    })]
  });
};